/**
 * useInstallNudge — Owns beforeinstallprompt listener and first-launch install flow.
 * Extracted from RetreatBridge lines 284-330.
 */
import { useState, useEffect, useRef, useCallback, type Dispatch } from "react";
import type { UIAction } from "../bridge/types";

export function useInstallNudge(
  dispatchUI: Dispatch<UIAction>,
  isFirstLaunch: boolean,
  demo: boolean,
): {
  handleInstallBridge: () => Promise<void>;
  handleInstallSkip: () => void;
  handleInstallDone: () => void;
  handleInstallBack: () => void;
  isAppInstalled: boolean;
} {
  const installPromptRef = useRef<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const wasFirstLaunch = useRef(isFirstLaunch);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      installPromptRef.current = e;
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Chrome fires appinstalled when the user adds the app to their home screen
  // (either via the browser's own prompt or after handleInstallBridge). Dismiss
  // the install flow and hide the install button permanently (MOB-07).
  useEffect(() => {
    const handler = () => {
      installPromptRef.current = null;
      setIsAppInstalled(true);
      dispatchUI({ type: "DISMISS_INSTALL" });
    };
    window.addEventListener("appinstalled", handler);
    return () => window.removeEventListener("appinstalled", handler);
  }, [dispatchUI]);

  useEffect(() => {
    const justCompletedFirstLaunch = wasFirstLaunch.current && !isFirstLaunch && !demo;
    if (justCompletedFirstLaunch) {
      const isStandalone = window.matchMedia(
        "(display-mode: standalone)",
      ).matches;
      if (!isStandalone) {
        dispatchUI({ type: "SHOW_INSTALL_BRIDGE" });
      }
    }
    wasFirstLaunch.current = isFirstLaunch;
  }, [isFirstLaunch, demo, dispatchUI]);

  const handleInstallBridge = useCallback(async () => {
    dispatchUI({ type: "SHOW_INSTALL_PROMPT" });
    if (installPromptRef.current) {
      try {
        const result = await (installPromptRef.current as any).prompt();
        // outcome: "accepted" | "dismissed"
        if (result?.outcome === "accepted") {
          installPromptRef.current = null;
          dispatchUI({ type: "DISMISS_INSTALL" });
        }
      } catch {
        // User dismissed the browser prompt — stay on install-prompt screen
      }
    }
  }, [dispatchUI]);

  const handleInstallSkip = useCallback(() => {
    dispatchUI({ type: "DISMISS_INSTALL" });
  }, [dispatchUI]);

  const handleInstallDone = useCallback(() => {
    dispatchUI({ type: "DISMISS_INSTALL" });
  }, [dispatchUI]);

  const handleInstallBack = useCallback(() => {
    dispatchUI({ type: "SHOW_INSTALL_BRIDGE" });
  }, [dispatchUI]);

  return {
    handleInstallBridge,
    handleInstallSkip,
    handleInstallDone,
    handleInstallBack,
    isAppInstalled,
  };
}
